import Joyride, { EVENTS, STATUS } from 'react-joyride';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { useStorybookContext } from '../../../Context/AppProvider';
import { AuthContext } from '../../../Context/authContext';
import { notifyTourStatusChange } from '../../../hooks/useDummyData';
import CustomTooltip from './CustomTooltip';
import TooltipContent from './Tooltip';
import userService from '../../../services/userService';
import Toast from '../../molecules/Toast';

// Optional: define per-step spotlight styles here (keyed by step index)
// You can customize each step's spotlight independently.
const STEP_SPOTLIGHT_STYLES = {
  2: {
    borderRadius: '23px',
  },
  9: {
    borderRadius: '23px',
  },
  14: {
    borderRadius: '60px',
  },
  18: {
    borderRadius: '50%',
  },
  19: {
    borderRadius: '50%',
  },
  // Add more indices here to style additional steps
};

const getSpotlightStyleForStep = (stepIndex) => STEP_SPOTLIGHT_STYLES[stepIndex] || {};

const SIDEBAR_TOUR_TARGET_IDS = new Set([
  '#dashboard',
  '#campaign-manager',
  '#unibox',
  '#emails-sidebar',
  '#leads-module',
  '#leads-list',
  '#import-jobs',
  '#pending-approvals',
  '#email-accounts',
  '#linkedin-accounts',
  '#teams',
  '#subscriptions-btn',
  '#help-center-btn',
]);

const normalizeTourSteps = (steps) =>
  steps.map((step) => {
    const withPlacement = step.placement === 'right'
      ? { ...step, spotlightPadding: 0 }
      : step;

    if (!SIDEBAR_TOUR_TARGET_IDS.has(step.target)) {
      return withPlacement;
    }

    return {
      ...withPlacement,
      disableScrolling: true,
    };
  });

const STEPS_REQUIRING_DOM_WAIT = new Set([
  '#emails-sidebar',
  '#start-new-campaign-btn',
  '#create-import-jobs-button',
  '#invite-team-member-btn',
]);

const TOUR_TARGET_WAIT_MS = 250;
const TOUR_TARGET_MAX_ATTEMPTS = 80;
const SIDEBAR_LAYOUT_SELECTORS = '.nav-wrapper, .help-center';
const SIDEBAR_MIN_TARGET_TOP = 100;
const SIDEBAR_TRANSITION_MS = 350;
const SIDEBAR_SCROLL_PADDING = 16;
const SIDEBAR_SCROLL_DURATION_MS = 500;
const JOYRIDE_SPOTLIGHT_SELECTOR = '.react-joyride__spotlight';
const NAV_SCROLL_SELECTOR = '.nav-scroll';

const getNavScrollContainer = () => document.querySelector(NAV_SCROLL_SELECTOR);

const isSidebarTourTarget = (element) =>
  Boolean(element?.closest(SIDEBAR_LAYOUT_SELECTORS));

const isInsideNavScroll = (element) => {
  const scrollContainer = getNavScrollContainer();
  return Boolean(element && scrollContainer?.contains(element));
};

const isTargetVisibleInNavScroll = (element) => {
  const scrollContainer = getNavScrollContainer();
  if (!scrollContainer?.contains(element)) {
    return true;
  }

  const elemRect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();

  return (
    elemRect.bottom > containerRect.top + SIDEBAR_SCROLL_PADDING
    && elemRect.top < containerRect.bottom - SIDEBAR_SCROLL_PADDING
  );
};

const shouldScrollSidebarTarget = (selector) => {
  if (!selector || typeof document === 'undefined') return false;
  const target = document.querySelector(selector);
  return isInsideNavScroll(target);
};

const scrollSidebarTargetIntoView = (selector) => new Promise((resolve) => {
  if (!selector || typeof document === 'undefined') {
    resolve();
    return;
  }

  const target = document.querySelector(selector);
  const scrollContainer = getNavScrollContainer();

  if (!target) {
    resolve();
    return;
  }

  if (!scrollContainer?.contains(target)) {
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    window.setTimeout(resolve, SIDEBAR_SCROLL_DURATION_MS);
    return;
  }

  if (isTargetVisibleInNavScroll(target)) {
    resolve();
    return;
  }

  const targetRect = target.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const containerHeight = scrollContainer.clientHeight;
  const targetTop = targetRect.top - containerRect.top + scrollContainer.scrollTop;
  const targetBottom = targetTop + targetRect.height;

  let nextScrollTop = scrollContainer.scrollTop;

  if (targetRect.top < containerRect.top + SIDEBAR_SCROLL_PADDING) {
    nextScrollTop = targetTop - SIDEBAR_SCROLL_PADDING;
  } else if (targetRect.bottom > containerRect.bottom - SIDEBAR_SCROLL_PADDING) {
    nextScrollTop = targetBottom - containerHeight + SIDEBAR_SCROLL_PADDING;
  }

  const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - containerHeight);
  nextScrollTop = Math.max(0, Math.min(nextScrollTop, maxScrollTop));

  if (Math.abs(nextScrollTop - scrollContainer.scrollTop) < 2) {
    resolve();
    return;
  }

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    scrollContainer.removeEventListener('scrollend', finish);
    window.clearTimeout(fallbackTimer);
    resolve();
  };

  scrollContainer.addEventListener('scrollend', finish, { once: true });
  const fallbackTimer = window.setTimeout(finish, SIDEBAR_SCROLL_DURATION_MS);

  scrollContainer.scrollTo({ top: nextScrollTop, behavior: 'smooth' });
});

const prepareSidebarTourStep = async (selector, padding = 0) => {
  if (shouldScrollSidebarTarget(selector)) {
    await scrollSidebarTargetIntoView(selector);
  }

  syncSidebarSpotlight(selector, padding);
};

const isSidebarLayoutReady = () => {
  if (typeof window === 'undefined') return true;

  if (window.innerWidth < 992 && !document.body.classList.contains('aside-active')) {
    return false;
  }

  const navWrapper = document.querySelector('.nav-wrapper');
  if (!navWrapper) return false;

  const { left } = navWrapper.getBoundingClientRect();
  return left >= 0;
};

const hasValidTourTargetPosition = (element) => {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;

  if (!isSidebarTourTarget(element)) {
    return true;
  }

  if (!isSidebarLayoutReady()) {
    return false;
  }

  if (rect.left < 0) {
    return false;
  }

  if (element.closest('.help-center')) {
    return true;
  }

  if (isInsideNavScroll(element)) {
    return isTargetVisibleInNavScroll(element);
  }

  return rect.top >= SIDEBAR_MIN_TARGET_TOP;
};

const isTourTargetReady = (selector) => {
  if (!selector || typeof document === 'undefined') return false;

  const element = document.querySelector(selector);
  if (!element) return false;

  return hasValidTourTargetPosition(element);
};

const syncSidebarSpotlight = (selector, padding = 0) => {
  if (!selector || typeof document === 'undefined') return;

  const target = document.querySelector(selector);
  const spotlight = document.querySelector(JOYRIDE_SPOTLIGHT_SELECTOR);
  if (!target || !spotlight || !isSidebarTourTarget(target)) return;

  const rect = target.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  spotlight.style.position = 'fixed';
  spotlight.style.top = `${Math.round(rect.top - padding)}px`;
  spotlight.style.left = `${Math.round(rect.left - padding)}px`;
  spotlight.style.width = `${Math.round(rect.width + padding * 2)}px`;
  spotlight.style.height = `${Math.round(rect.height + padding * 2)}px`;
};

const waitForTourTarget = (selector, onReady, onTimeout) => {
  let attempts = 0;
  let isPreparing = false;

  const checkElement = setInterval(() => {
    attempts += 1;

    const element = document.querySelector(selector);
    if (!element) {
      if (attempts >= TOUR_TARGET_MAX_ATTEMPTS) {
        clearInterval(checkElement);
        onTimeout();
      }
      return;
    }

    if (shouldScrollSidebarTarget(selector) && !isTargetVisibleInNavScroll(element) && !isPreparing) {
      isPreparing = true;
      scrollSidebarTargetIntoView(selector).finally(() => {
        isPreparing = false;
      });
      return;
    }

    if (isPreparing) {
      return;
    }

    if (isTourTargetReady(selector)) {
      clearInterval(checkElement);
      onReady();
      return;
    }

    if (attempts >= TOUR_TARGET_MAX_ATTEMPTS) {
      clearInterval(checkElement);
      onTimeout();
    }
  }, TOUR_TARGET_WAIT_MS);

  return checkElement;
};

const resumeTourAfterSidebarPrep = (setState, selector, padding = 0) => {
  prepareSidebarTourStep(selector, padding).then(() => {
    window.setTimeout(() => {
      setState({ run: true });
    }, 100);
  });
};

const pauseTourForStep = (setState, stepIndex) => {
  setState({ run: false, stepIndex });
};

const shouldSkipTeamsDirectToHelpCenter = () => {
  try {
    if (typeof document !== 'undefined') {
      const inviteTeamMemberBtn = document.querySelector('#invite-team-member-btn');
      const subscriptionsBtn = document.querySelector('#subscriptions-btn');
      return !inviteTeamMemberBtn && !subscriptionsBtn;
    }
  } catch (e) {
    // ignore and fall through
  }

  return false;
};

const getTeamsStepAvailability = () => {
  try {
    if (typeof document !== 'undefined') {
      const inviteTeamMemberBtn = document.querySelector('#invite-team-member-btn');
      const subscriptionsBtn = document.querySelector('#subscriptions-btn');
      return {
        hasInviteTeamMemberBtn: Boolean(inviteTeamMemberBtn),
        hasSubscriptionsBtn: Boolean(subscriptionsBtn),
      };
    }
  } catch (e) {
    // ignore and fall through
  }

  return {
    hasInviteTeamMemberBtn: false,
    hasSubscriptionsBtn: false,
  };
};

const RAW_TOUR_STEPS = [
  {
    target: '#dashboard',
    content: (
      <TooltipContent
        title="Dashboard"
        description="Learn about Houdiny, get started quickly, and track campaign performance."
      />
    ),
    placement: 'right',
    data: {
      next: '/campaign-manager',
    },
    disableBeacon: true,
  },
  // {
  //   target: '#get-started-btn',
  //   content: (
  //     <TooltipContent
  //       title="Quick Houdiny Intro"
  //       description="Watch the video tutorials to become an outreach expert."
  //     />
  //   ),
  //   data: {
  //     next: '/dashboard',
  //   },
  // },
  // {
  //   target: '#dashboard-btn',
  //   content: (
  //     <TooltipContent
  //       title="Dashboard / Reports"
  //       description="Overview of your campaign metrics. See how good your campaigns run."
  //     />
  //   ),
  //   data: {
  //     previous: '/get-started',
  //     next: '/campaign-manager',
  //   },
  // },
  {
    target: '#campaign-manager',
    content: (
      <TooltipContent
        title="Campaigns"
        description="Create email and LinkedIn campaigns using AI features."
      />
    ),
    placement: 'right',
    data: {
      previous: '/dashboard',
    },
  },
  {
    target: '#start-new-campaign-btn',
    content: (
      <TooltipContent
        title="Create Campaign"
        description="Click here to create a new campaign."
      />
    ),
    spotlightPadding: 5,
    placement: 'left',
  },
  {
    target: '#all-campaigns',
    content: (
      <TooltipContent
        title="Your Campaigns"
        description="Overview of your campaigns. By pressing the pencil you can edit it."
      />
    ),
    spotlightPadding: 1,
    placement: 'top-end',
    data: {
      next: '/unibox',
    },
  },
  {
    target: '#unibox',
    content: (
      <TooltipContent
        title="Unibox"
        description="One inbox for all of your email accounts and campaigns."
      />
    ),
    placement: 'right',
    data: {
      previous: '/campaign-manager',
      stayOnPage: true,
    },
  },
  {
    target: '#emails-sidebar',
    content: (
      <TooltipContent
        title="Messages"
        description="See who replied to your messages. Filter by Replied or Outgoing and continue the thread inside Houdiny."
      />
    ),
    spotlightPadding: 1,
    placement: 'right',
    disableScrolling: true,
    data: {
      previous: '/unibox',
      next: '/leads-module',
    },
  },
  {
    target: '#leads-module',
    content: (
      <TooltipContent
        title="Contacts"
        description="View and manage all contacts in your Houdiny account."
      />
    ),
    placement: 'right',
    data: {
      previous: '/unibox',
      next: '/leads-list',
    },
  },
  {
    target: '#leads-list',
    content: (
      <TooltipContent
        title="List Builder"
        description="Build lead lists using advanced filters for successful campaigns."
      />
    ),
    placement: 'right',
    data: {
      previous: '/leads-module',
      next: '/import-jobs',
    },
  },
  {
    target: '#import-jobs',
    content: (
      <TooltipContent
        title="Add Leads"
        description="Upload contacts or scrape LinkedIn here."
      />
    ),
    placement: 'right',
    data: {
      previous: '/leads-list',
    },
  },
  {
    target: '#create-import-jobs-button',
    content: (
      <TooltipContent
        title="Create New Import"
        description="Click here to create a new lead import."
      />
    ),
    data: {
      next: '/pending-approvals',
    },
  },
  {
    target: '#pending-approvals',
    content: (
      <TooltipContent
        title="Pending Approvals"
        description="Review and approve AI-generated messages before they are sent."
      />
    ),
    placement: 'right',
    disableScrolling: true,
    data: {
      previous: '/import-jobs',
      next: '/email-accounts',
    },
  },
  {
    target: '#email-accounts',
    content: (
      <TooltipContent
        title="Email Accounts"
        description="Connect your email accounts and manage domains."
      />
    ),
    placement: 'right',
    data: {
      previous: '/pending-approvals',
      next: '/linkedin-accounts',
    },
  },
  {
    target: '#linkedin-accounts',
    content: (
      <TooltipContent
        title="LinkedIn Accounts"
        description="Connect your LinkedIn accounts here."
      />
    ),
    placement: 'right',
    data: {
      previous: '/email-accounts',
      next: '/teams',
    },
  },
  {
    target: '#teams',
    content: (
      <TooltipContent
        title="Teams"
        description="Invite team members and manage your team."
      />
    ),
    placement: 'right',
    data: {
      previous: '/linkedin-accounts',
    },
  },
  {
    target: '#invite-team-member-btn',
    content: (
      <TooltipContent
        title="Invite Team Member"
        description="Click here to invite your team member and assign them a plan."
      />
    ),
    spotlightPadding: 4,
    data: {
      nextState: { activeTeamTab: 'invitation' },
    },
  },
  {
    target: '#invitations-btn',
    content: (
      <TooltipContent
        title="Invited Team Members"
        description="See all pending invitations."
      />
    ),
    data: {
      previousState: { activeTeamTab: 'team' },
    },
  },
  {
    target: '#subscriptions-btn',
    content: (
      <TooltipContent
        title="Subscription"
        description="See your subscription. Upgrade, downgrade or cancel here."
      />
    ),
    spotlightPadding: 0,
    placement: 'right',
  },
  {
    target: '#help-center-btn',
    content: (
      <TooltipContent
        title="Help Center"
        description="For any additional questions, check the help center page."
      />
    ),
    spotlightPadding: 4,
  },
];

const TOUR_STEPS = normalizeTourSteps(RAW_TOUR_STEPS);

const OnboardingTour = ({ children }) => {
  const {
    setState,
    state: { run, stepIndex, steps, tourActive },
  } = useStorybookContext();

  const { user, authReady } = useContextHook(AuthContext, v => ({
    user: v.user,
    authReady: v.authReady,
  }));

  const navigate = useNavigate();
  const location = useLocation();
  const helpCenterBackTargetRef = useRef(null);
  const sidebarScrollPrepRef = useRef(null);

  useEffect(() => {
    if (!tourActive && !authReady) {
      return;
    }
    const hasSeenTour = user?.data?.tour_completed || localStorage.getItem('hasSeenTour');
    if ((!hasSeenTour && steps.length === 0) || (tourActive && steps.length === 0)) {
      setState({
        steps: TOUR_STEPS,
      });
    }
  }, [tourActive, steps.length, setState, user, authReady]);

  useEffect(() => {
    if (!tourActive && !authReady) {
      return undefined;
    }
    const hasSeenTour = user?.data?.tour_completed || localStorage.getItem('hasSeenTour');
    if ((!hasSeenTour || tourActive) && steps.length > 0 && !run) {
      const firstTarget = steps[0]?.target;
      const checkElement = setInterval(() => {
        if (isTourTargetReady(firstTarget)) {
          setState({ run: true, tourActive: true });
          clearInterval(checkElement);
        }
      }, 300);

      return () => clearInterval(checkElement);
    }

    return undefined;
  }, [steps, run, setState, tourActive, user, authReady]);

  useEffect(() => {
    if (!run || stepIndex < 0 || stepIndex >= steps.length) {
      return undefined;
    }

    const currentStep = steps[stepIndex];
    const selector = currentStep?.target;
    if (!selector || !SIDEBAR_TOUR_TARGET_IDS.has(selector)) {
      return undefined;
    }

    const padding = currentStep.spotlightPadding ?? 0;
    let frameId = 0;

    const scheduleSync = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        syncSidebarSpotlight(selector, padding);
      });
    };

    scheduleSync();
    const intervalId = setInterval(scheduleSync, 200);
    const transitionTimer = setTimeout(scheduleSync, SIDEBAR_TRANSITION_MS);

    window.addEventListener('resize', scheduleSync);
    const navScroll = getNavScrollContainer();
    navScroll?.addEventListener('scroll', scheduleSync, { passive: true });

    const target = document.querySelector(selector);
    let resizeObserver;
    if (target && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleSync);
      resizeObserver.observe(target);
    }

    return () => {
      cancelAnimationFrame(frameId);
      clearInterval(intervalId);
      clearTimeout(transitionTimer);
      window.removeEventListener('resize', scheduleSync);
      navScroll?.removeEventListener('scroll', scheduleSync);
      resizeObserver?.disconnect();
    };
  }, [run, stepIndex, steps]);

  // Resume tour after navigation or when waiting for in-page targets (e.g. Unibox sidebar)
  useEffect(() => {
    if (!tourActive || run || stepIndex < 0 || stepIndex >= steps.length || steps.length === 0) {
      return undefined;
    }

    const currentStep = steps[stepIndex];
    if (!currentStep?.target) {
      return undefined;
    }

    const intervalId = waitForTourTarget(
      currentStep.target,
      () => {
        resumeTourAfterSidebarPrep(
          setState,
          currentStep.target,
          currentStep.spotlightPadding ?? 0,
        );
      },
      () => {
        if (STEPS_REQUIRING_DOM_WAIT.has(currentStep.target)) {
          // Keep waiting for in-page targets such as the Unibox messages sidebar.
          waitForTourTarget(
            currentStep.target,
            () => {
              resumeTourAfterSidebarPrep(
                setState,
                currentStep.target,
                currentStep.spotlightPadding ?? 0,
              );
            },
            () => {
              console.warn(`Tour: Could not find target "${currentStep.target}"`);
            },
          );
          return;
        }

        console.warn(`Tour: Could not find target "${currentStep.target}"`);
        setState({ run: true });
      },
    );

    return () => clearInterval(intervalId);
  }, [location.pathname, run, stepIndex, steps, setState, tourActive]);

  const handleCallback = async (data) => {
    const { action, index, status, type, step } = data;

    if (type === EVENTS.STEP_BEFORE) {
      const selector = step?.target;
      const padding = step?.spotlightPadding ?? 0;
      const target = selector ? document.querySelector(selector) : null;
      const needsScroll = target && shouldScrollSidebarTarget(selector)
        && !isTargetVisibleInNavScroll(target);

      if (needsScroll) {
        if (sidebarScrollPrepRef.current) {
          sidebarScrollPrepRef.current.cancelled = true;
        }

        const prep = { cancelled: false };
        sidebarScrollPrepRef.current = prep;

        setState({ run: false });

        prepareSidebarTourStep(selector, padding).then(() => {
          if (prep.cancelled) return;
          setState({ run: true });
        });
      } else if (selector && SIDEBAR_TOUR_TARGET_IDS.has(selector)) {
        requestAnimationFrame(() => {
          syncSidebarSpotlight(selector, padding);
        });
      }

      return;
    }

    if (type === EVENTS.TOOLTIP) {
      requestAnimationFrame(() => {
        syncSidebarSpotlight(step?.target, step?.spotlightPadding ?? 0);
      });
    }

    // End tour
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      if (user?.data?.tour_completed) {
        localStorage.setItem('hasSeenTour', 'true');
        notifyTourStatusChange('hasSeenTour');
        setState({ run: false, stepIndex: 0, tourActive: false });
      } else {
        try {
          await userService.updateTourCompletion({ isCompleted: true });
          localStorage.setItem('hasSeenTour', 'true');
          notifyTourStatusChange('hasSeenTour');
          setState({ run: false, stepIndex: 0, tourActive: false });
        } catch (error) {
          Toast({ type: 'error', message: error.message || 'Failed to update tour completion' });
        }
      }

      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const missingStep = steps[index];
      if (missingStep?.target && STEPS_REQUIRING_DOM_WAIT.has(missingStep.target) && action !== 'prev') {
        setState({ run: false, stepIndex: index });
        return;
      }
    }

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      const isPreviousAction = action === 'prev';
      const currentStep = steps[index];
      const { next, previous, nextState, previousState } = (currentStep && currentStep.data) || {};
      const { hasInviteTeamMemberBtn, hasSubscriptionsBtn } = getTeamsStepAvailability();
      const skipTeamsDirectToHelpCenter = !hasInviteTeamMemberBtn && !hasSubscriptionsBtn;
      const skipOnlySubscriptionStep = hasInviteTeamMemberBtn && !hasSubscriptionsBtn;

      // Calculate new step index
      let newIndex;
      if (isPreviousAction) {
        if (helpCenterBackTargetRef.current === 'teams' && currentStep?.target === '#help-center-btn' && index >= 4) {
          // If we jumped from Teams directly to Help Center, go back to Teams.
          newIndex = Math.max(index - 4, 0);
        } else if (
          helpCenterBackTargetRef.current === 'invitations' &&
          currentStep?.target === '#help-center-btn' &&
          index >= 2
        ) {
          // If we jumped from Invitations directly to Help Center, go back to Invitations.
          newIndex = Math.max(index - 2, 0);
        } else {
          newIndex = Math.max(index - 1, 0);
        }
      } else if (!isPreviousAction && currentStep?.target === '#unibox') {
        // Stay on /unibox and show the messages sidebar tooltip before Contacts.
        pauseTourForStep(setState, index + 1);
        return;
      } else if (skipTeamsDirectToHelpCenter && currentStep?.target === '#teams') {
        // If both Invite Team Member and Subscription buttons are unavailable,
        // skip directly from Teams to Help Center.
        helpCenterBackTargetRef.current = 'teams';
        newIndex = index + 4;
      } else if (skipOnlySubscriptionStep && currentStep?.target === '#invitations-btn') {
        // If only Subscription is unavailable, skip Invitations -> Help Center.
        helpCenterBackTargetRef.current = 'invitations';
        newIndex = index + 2;
      } else {
        newIndex = index + 1;
      }
      // The *current* step is the last one when its index equals steps.length - 1
      const isLastStep = index === steps.length - 1;

      // Determine route and state to navigate to
      const routeToGo = isPreviousAction ? previous : next;
      const routeState = isPreviousAction ? previousState : nextState;
      const isSamePath = routeToGo && routeToGo === location.pathname;
      const shouldStayOnPage = !isPreviousAction && currentStep?.data?.stayOnPage;

      if (shouldStayOnPage) {
        pauseTourForStep(setState, newIndex);
        return;
      }

      if (routeState || (routeToGo && !isSamePath)) {
        // We are navigating (either to a different route or updating state on the same route)
        setState({ run: false, stepIndex: newIndex });

        const targetPath = routeToGo || location.pathname;

        if (routeState) {
          navigate(targetPath, { state: routeState });
        } else if (routeToGo && !isSamePath) {
          navigate(routeToGo);
        }
      } else if (isLastStep && !isPreviousAction) {
        if (user?.data?.tour_completed) {
          localStorage.setItem('hasSeenTour', 'true');
          notifyTourStatusChange('hasSeenTour');
          setState({ run: false, stepIndex: 0, tourActive: false });
        } else {
          try {
            await userService.updateTourCompletion({ isCompleted: true });
            localStorage.setItem('hasSeenTour', 'true');
            notifyTourStatusChange('hasSeenTour');
            setState({ run: false, stepIndex: 0, tourActive: false });
          } catch (error) {
            Toast({ type: 'error', message: error.message || 'Failed to update tour completion' });
          }
        }
      } else {
        // No route change, just move to next/previous step
        const nextStep = steps[newIndex];
        if (nextStep?.target && STEPS_REQUIRING_DOM_WAIT.has(nextStep.target)) {
          setState({ run: false, stepIndex: newIndex });
        } else {
          setState({ stepIndex: newIndex });
        }
      }
    }
  };

  if (!steps || steps.length === 0) {
    return null;
  }

  const handleRestartTour = () => {
    // Clear completion flag and reset tour state
    localStorage.removeItem('hasSeenTour');
    notifyTourStatusChange('hasSeenTour');

    setState({
      steps: TOUR_STEPS,
      run: false,
      stepIndex: 0,
      tourActive: true,
    });
  };

  // Build Joyride styles dynamically so spotlight can be different per step
  const joyrideStyles = {
    options: {
      zIndex: 10000,
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      height: '100vh',
    },
    spotlight: {
      borderRadius: '8px',
      ...getSpotlightStyleForStep(stepIndex),
    },
  };

  return (
    <>
      <Joyride
        callback={handleCallback}
        continuous
        disableOverlayClose
        run={run}
        stepIndex={stepIndex}
        steps={steps}
        showSkipButton
        showProgress
        tooltipComponent={CustomTooltip}
        styles={joyrideStyles}
      >
      </Joyride>
    </>
  );
};

export default OnboardingTour;
